import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {SubscriptionService} from "../services/subscription-service";
import {useGlobalSnackbarStore} from "../stores/global-snackbar-store";
import {DateTime} from "luxon";

type SubscribeOpts = {
    duration: number;
    start?: DateTime;
};

export const useAdminSubscriptions = () => {
    const {error, success} = useGlobalSnackbarStore((state) => state);
    const queryClient = useQueryClient();

    const queryInfo = useQuery({
        queryKey: ['subscriptions'],
        queryFn: async () => await SubscriptionService.list(),
    });

    const invalidateSubscriptionQueries = async () => await Promise.all([
        queryClient.invalidateQueries({queryKey: ['subscription']}),
        queryClient.invalidateQueries({queryKey: ['subscriptions']}),
    ]);

    const {mutateAsync: subscribeBreeder, isPending: isSubscribeBreederPending} = useMutation({
        mutationFn: async ({breederId, opts}: {
            breederId: string,
            opts: SubscribeOpts,
        }) => await SubscriptionService.subscribeBreeder(breederId, opts),
        onSuccess: () => success('Subscription angelegt.'),
        onError: () => error('Fehler beim Anlegen der Subscription!'),
        onSettled: invalidateSubscriptionQueries,
    });

    const {mutateAsync: unsubscribeBreeder, isPending: isUnsubscribeBreederPending} = useMutation({
        mutationFn: async (breederId: string) => await SubscriptionService.unsubscribeBreeder(breederId),
        onSuccess: () => success('Subscription entfernt.'),
        onError: () => error('Fehler beim Entfernen der Subscription!'),
        onSettled: invalidateSubscriptionQueries,
    });

    const {mutateAsync: subscribeAssociation, isPending: isSubscribeAssociationPending} = useMutation({
        mutationFn: async ({registryId, opts}: {
            registryId: string,
            opts: SubscribeOpts,
        }) => await SubscriptionService.subscribeAssociation(registryId, opts),
        onSuccess: () => success('Subscription angelegt.'),
        onError: () => error('Fehler beim Anlegen der Subscription!'),
        onSettled: invalidateSubscriptionQueries,
    });

    const {mutateAsync: unsubscribeAssociation, isPending: isUnsubscribeAssociationPending} = useMutation({
        mutationFn: async (registryId: string) => await SubscriptionService.unsubscribeAssociation(registryId),
        onSuccess: () => success('Subscription entfernt.'),
        onError: () => error('Fehler beim Entfernen der Subscription!'),
        onSettled: invalidateSubscriptionQueries,
    });

    return {
        ...queryInfo,
        get subscriptions() { return queryInfo.data; },
        subscribeBreeder,
        isSubscribeBreederPending,
        unsubscribeBreeder,
        isUnsubscribeBreederPending,
        subscribeAssociation,
        isSubscribeAssociationPending,
        unsubscribeAssociation,
        isUnsubscribeAssociationPending,
    };
};